/** @jsx jsx */
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock'
import { Alert } from 'components/Alert'
import Meta from 'components/Meta'
import { motion } from 'framer-motion'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useRef, useEffect } from 'react'
import { jsx, Container, Flex, Text } from 'theme-ui'
import { FavouriteButton } from 'components/buttons/FavouriteButton'
import { MapButton } from './buttons/MapButton'

const eventVariants = {
  exit: {
    x: '100%',
    opacity: 1,
    transition: {
      type: 'spring',
      damping: 20,
      stiffness: 150,
      mass: 0.5,
      restDelta: 0.001,
    },
  },
  enter: {
    x: '0%',
    opacity: 1,
    transition: {
      type: 'spring',
      damping: 20,
      stiffness: 150,
      mass: 0.5,
      restDelta: 0.001,
    },
  },
}

const backgroundVariants = {
  initial: {},
  exit: {
    visibility: 'hidden',
    pointerEvents: 'none',
    // display: "none",
  },
  enter: {
    transitionEnd: {
      // display block
      // pointerEvents: "all",
    },
  },
}

const PageLayout = ({
  metaFields = {},
  isCorrectSlug,
  preview = false,
  children,
  eventId = undefined,
  containerVariant = 'container',
  width,
  maxWidth,
  tnew,
  mapState,
  setMapState,
  ...props
}) => {
  const router = useRouter()
  // console.log(mapState, setMapState)
  const pageRef = useRef()
  useEffect(() => {
    disableBodyScroll(pageRef.current)
    return () => clearAllBodyScrollLocks()
  }, [])

  return (
    <>
      {isCorrectSlug && <Meta metaFields={metaFields} />}
      <Alert preview={preview} />
      <motion.div
        sx={{
          width: '100%',
          height: '100%',
          position: 'fixed',
          top: 0,
          left: 0,
          zIndex: 999,
          // background: "blue", // blue background for debugging
          ':hover': {
            cursor: 'pointer',
          },
        }}
        variants={backgroundVariants}
        initial="enter"
        animate="enter"
        exit="exit"
        onClick={() => {
          clearAllBodyScrollLocks()
          router.push('/program', undefined, { scroll: false })
        }}
      />
      <motion.main
        ref={pageRef}
        variants={eventVariants}
        initial="exit"
        animate="enter"
        exit="exit"
        sx={{
          position: 'fixed',
          top: 0,
          right: 0,
          zIndex: 1000,
          width: width || ['100%', null, null, '80%'],
          maxWidth: maxWidth || '1600px',
          height: '100%',
          pb: 6,
          bg: 'primary',
          color: 'secondary',
          background: 'black',
          overflowY: 'auto',
          overflowX: 'hidden',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'black',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'red',
          },
          '::selection': {
            color: 'black',
            backgroundColor: 'red',
          },
        }}
        data-current-page={true}
        {...props}
      >
        <Container
          pt={containerVariant === 'container' && [2]}
          pb={containerVariant === 'container' && [2]}
          variant={containerVariant}
        >
          <Flex
            sx={{
              justifyContent:
                eventId || mapState ? 'space-between' : 'flex-end',
            }}
          >
            {mapState && setMapState && (
              <Flex>
                <MapButton
                  selected={mapState === 'dark-mofo'}
                  onClick={() => setMapState('dark-mofo')}
                >
                  Dark Mofo
                </MapButton>
                <MapButton
                  selected={mapState === 'off-season'}
                  onClick={() => setMapState('off-season')}
                  sx={{ ml: [2, 2, null, 2, 3] }}
                  // ml={['3px', null, null, '4px', '5px']}
                >
                  Off Season
                </MapButton>
              </Flex>
            )}
            {eventId && (
              <FavouriteButton
                color="red"
                id={eventId}
                style="textandicon"
                tnew={tnew}
              />
            )}
            <Link href="/program" scroll={false}>
              <Text
                as="button"
                variant="smallCopySmallCapsReverse"
                onClick={() => clearAllBodyScrollLocks()}
              >
                (close)
              </Text>
            </Link>
          </Flex>
          {children}
        </Container>
      </motion.main>
    </>
  )
}

export { PageLayout }
